'use client'

import Header from '@components/header'
import type { HeaderProps } from '@components/header'
import Footer from '@components/footer'
import type { FooterProps } from '@components/footer'
import { site, twitter } from '@lib/constants'
import ContactUs from '@components/contact-us'
import AlertBar from '@components/alert-bar'
import { Montserrat } from 'next/font/google'
import { Container, Alert, Typography, Link } from '@shc/ui'
import SessionProvider from '@components/session-provider'
import UserAnalyticsWrapper from '@components/user-analytics-wrapper'
import { extractUserSessionForAnalytics } from '@lib/analytics'
import type { TypeAlertFields } from '@lib/generated-types'
import AlertSpoofing from '@components/alert-spoofing'

const montserrat = Montserrat({ subsets: ['latin'], variable: '--font-montserrat' })
 
export interface MainLayoutProps {
  preview: boolean
  theme: string
  header: HeaderProps
  footer: FooterProps
  children: React.ReactNode
  title?: string
  description?: string
  seoTitle?: string
  seoDescription?: string
  session: any
  alerts: []
}

export const MainLayout = ({
  preview,
  theme,
  header,
  footer,
  children,
  title,
  description,
  seoTitle,
  seoDescription,
  session,
  alerts,
}: MainLayoutProps) => {
  const titleFinal = `${seoTitle ?? title ?? site.name} | ${site.title}`
  const descriptionFinal = seoDescription ?? description ?? site.description

  const userInfo = session?.user?.userProfile ? extractUserSessionForAnalytics(session?.user?.userProfile) : null

  return (
      <body className={montserrat.variable} data-theme={theme}>
      <SessionProvider session={session}>
        <UserAnalyticsWrapper value={userInfo}>
        <title>{titleFinal}</title>
        <meta key="description" name="description" content={descriptionFinal} />

        {/* Twitter */}
        <meta key="twitterSite" name="twitter:site" content={twitter.site} />

        {/* Open Graph */}
        <meta key="ogTitle" property="og:title" content={titleFinal} />
        <meta key="ogDescription" property="og:description" content={descriptionFinal} />

        { preview && (
          <Container className="mt-3">
            <Alert>
              <Typography>
                PREVIEW session started; review unpublished changes or <Link href="/custom-api/endpreview">end preview session</Link>.
              </Typography>
            </Alert>
          </Container>
        )}

        {alerts ? alerts.map(({ fields }, index) => {
          const alertFields = fields as TypeAlertFields
          
          return (
            <Container
              key={index}>
              <AlertBar
                className={index === alerts.length - 1 ? 'mb-3' : ''}
                description={alertFields.description}
                icon={alertFields.icon}
              />
            </Container>
          )
        }) : null }

        { session?.user?.isSpoofing && (
          <Container className="mt-3">
            <AlertSpoofing spoofedName={`${session?.user?.userProfile.firstname} ${session?.user?.userProfile.lastname}`} />
          </Container>
        )}
        <Header
          {...header}
          className={`font-sans`}
          >
        </Header>
        <main className={`flex-grow font-sans`}>{children}</main>
        <Footer {...footer} className={`font-sans`}>
          <ContactUs section="footer" contactUsLink={header?.activePathProviders && header?.loggedIn ? '/providers/contact-us' : undefined } />
        </Footer>
      </UserAnalyticsWrapper>
    </SessionProvider>
    </body>
  )
}

export default MainLayout
